// Legal documents content

export const LEGAL_TEXT = {
  terms: `TERMS AND CONDITIONS

1. INTRODUCTION AND ACCEPTANCE
Welcome to Expert Arena ("Platform"), a digital freelancing hiring portal operated by Aakar Co. ("Company," "we," "us," or "our"). These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and the Company.

By accessing, using, or registering on the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must immediately cease using the Platform and discontinue your account.

The Company reserves the right to modify these Terms at any time. Continued use of the Platform following any modifications constitutes your acceptance of the revised Terms. We will notify users of significant changes via email or through the Platform.

2. PLATFORM USAGE AND CONDUCT
The Platform is designed to facilitate professional connections between freelancers and clients seeking services. Users agree to use the Platform in compliance with all applicable laws, regulations, and these Terms.

You agree not to engage in any of the following prohibited conduct:
- Harassment, discrimination, or abusive behavior toward other users
- Posting false, misleading, or fraudulent information
- Attempting to gain unauthorized access to the Platform or other users' accounts
- Interfering with the Platform's operation or security systems
- Spamming, phishing, or distributing malware
- Violating intellectual property rights of others
- Engaging in unlawful activities or transactions
- Soliciting transactions outside the Platform to evade fees or oversight

3. INTELLECTUAL PROPERTY RIGHTS
All content created on the Platform, including but not limited to project descriptions, service offerings, profiles, and communications, remains the property of the respective creator. However, by posting content on the Platform, you grant the Company a worldwide, royalty-free, non-exclusive license to use, reproduce, display, and distribute your content for operational and promotional purposes.

You represent and warrant that any content you provide does not infringe upon third-party intellectual property rights. You agree to indemnify the Company against any claims arising from your content or conduct.

4. DISCLAIMER OF WARRANTIES
The Platform is provided on an "as-is" basis without warranties of any kind, express or implied. The Company makes no representations regarding the accuracy, completeness, or reliability of any information on the Platform.

The Company does not guarantee that the Platform will be error-free, uninterrupted, or meet your specific requirements. Users access and use the Platform at their own risk.

5. LIMITATION OF LIABILITY
To the fullest extent permitted by law, the Company shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform, even if the Company has been advised of the possibility of such damages.

The Company's total liability shall not exceed the amount you have paid to the Company in the twelve months preceding the claim, or one hundred dollars, whichever is greater.

6. TERMINATION OF SERVICE
The Company reserves the right to terminate or suspend your account and access to the Platform at any time, for any reason, without prior notice or liability. Grounds for termination include but are not limited to violation of these Terms, illegal activity, or conduct harmful to other users.

Upon termination, all rights and licenses granted to you are immediately revoked, and you must cease all use of the Platform.

7. DISPUTE RESOLUTION
Any disputes arising from these Terms or your use of the Platform shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law principles.

Both parties agree to first attempt resolution through informal negotiation. If negotiation fails, disputes shall be subject to binding arbitration in accordance with Indian arbitration law.

8. SEVERABILITY AND ENTIRE AGREEMENT
If any provision of these Terms is found to be invalid or unenforceable, such provision shall be severable and shall not affect the validity of the remaining provisions.

These Terms, together with our Privacy Policy and any additional terms posted on the Platform, constitute the entire agreement between you and the Company regarding your use of the Platform.`,

  privacy: `PRIVACY POLICY

1. INFORMATION WE COLLECT
Expert Arena ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your personal data.

We collect the following categories of information:
- Personal Identification: Name, email address, phone number, and postal address
- Professional Information: Designation, work experience, qualifications, and portfolio links
- Financial Information: Bank account details for payments, including account number and bank code
- Usage Data: Pages visited, time spent on Platform, click patterns, and device information
- Communication Data: Messages sent through the Platform and support inquiries
- Technical Data: IP addresses, browser type, operating system, and cookies

2. HOW WE USE YOUR INFORMATION
Your information is used for the following purposes:
- Service Delivery: Creating and maintaining your account, processing payments, and connecting you with opportunities
- Verification: Verifying your identity, qualifications, and bank details for payment processing
- Communication: Sending transactional emails, account notifications, and policy updates
- Platform Improvement: Analyzing usage patterns to enhance features and user experience
- Legal Compliance: Complying with tax obligations, fraud prevention, and legal requirements
- Marketing: With your consent, sending promotional emails about new features and opportunities

3. COOKIES AND TRACKING TECHNOLOGIES
The Platform uses cookies and similar tracking technologies to enhance user experience. Cookies are small files stored on your device that help us remember your preferences and track navigation patterns.

Types of cookies used:
- Essential Cookies: Required for Platform functionality and security
- Preference Cookies: Store your language and display preferences
- Analytics Cookies: Track usage patterns to improve the Platform
- Marketing Cookies: Enable targeted advertising and promotion

You can control cookie settings through your browser preferences. However, disabling essential cookies may impair Platform functionality.

4. THIRD-PARTY DATA SHARING
We may share your information with:
- Payment Processors: To process payments and verify bank details
- Cloud Service Providers: For data storage and backup
- Legal Authorities: When required by law or to prevent illegal activity
- Service Partners: To deliver specific services you request

We do not sell personal information to third parties. Any third-party sharing is limited to what is necessary for service delivery and is governed by confidentiality agreements.

5. DATA RETENTION
Personal information is retained for as long as your account remains active or as necessary to provide services. After account termination, we retain information as required by law or for legitimate business purposes, typically for three to seven years.

Financial and transaction data is retained for tax and audit purposes in accordance with applicable regulations.

6. YOUR PRIVACY RIGHTS
You have the following rights regarding your personal data:
- Access: Request a copy of all personal information we hold about you
- Correction: Request corrections to inaccurate information
- Deletion: Request deletion of your data, subject to legal retention requirements
- Portability: Request your data in a portable format
- Restriction: Request that we limit how we use your data
- Objection: Object to specific uses of your data

To exercise these rights, contact us at privacy@expertarena.com with your request and proof of identity.

7. DATA SECURITY
We implement industry-standard security measures including encryption, secure servers, and regular security audits to protect your information. However, no security system is completely impenetrable, and we cannot guarantee absolute security.

8. INTERNATIONAL DATA TRANSFERS
Your information may be transferred to and stored in countries other than your country of residence. By using the Platform, you consent to the transfer and processing of your data internationally.

9. CONTACT US
If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us at:
Email: privacy@expertarena.com
Address: Aakar Co., India

10. CHANGES TO THIS POLICY
We may update this Privacy Policy periodically. Continued use of the Platform following any updates constitutes your acceptance of the revised policy.`,

  agreement: `FREELANCER ENGAGEMENT AND SERVICES AGREEMENT

1. AGREEMENT OVERVIEW
This Freelancer Engagement and Services Agreement ("Agreement") is entered into between Expert Arena ("Platform"), operated by Aakar Co., and the undersigned individual freelancer ("Freelancer").

This Agreement governs the Freelancer's use of the Platform to provide services to clients, the compensation structure, and the terms of engagement.

2. FREELANCER ENGAGEMENT TERMS
The Freelancer acknowledges that by accepting this Agreement and using the Platform, they are engaging as an independent contractor. The Platform does not establish an employer-employee relationship, and the Freelancer is not entitled to employment benefits.

The Freelancer retains complete autonomy in determining how, when, and where services are delivered, provided they meet client requirements and Platform standards. The Freelancer is responsible for all expenses related to service delivery, including equipment, software, and internet connectivity.

3. COMMISSION AND PAYMENT
The Freelancer will earn a commission based on converted leads:
- Base Commission Rate: 10% of deal amount
- Premium Commission Rate: 15% (when Freelancer completes 10+ services in any month)
All commission rates are deducted from the freelancer's earnings before payment.

4. COMMISSION AND FEE STRUCTURE
The Platform charges a commission on all earnings generated through service engagements. The commission structure is as follows:

Base Commission: 10% of gross earnings on all completed projects
Loyalty Commission Reduction: Freelancers who achieve 10 or more successful conversions within a calendar year are eligible for a increased rate of 15% (subject to change based on account performance)
Payout Processing Fee: Additional service fees may apply for certain payment methods or cross-border transfers

All commission rates are deducted from earnings before payment to the Freelancer. The Freelancer acknowledges that these rates are subject to modification with 15 days' written notice.

5. EMPLOYMENT CLASSIFICATION
The Freelancer is an independent contractor and may be engaged as a performance-based employee of AAKAR Co. This arrangement is performance-based and earnings are not guaranteed.

6. PAYMENT TERMS AND CONDITIONS
Payments are processed through the Platform's payment system to the Freelancer's verified bank account. Standard payment terms are as follows:

Payment Cycle: Earnings are accumulated and paid out monthly on the specified payout date
Minimum Payout Threshold: A minimum balance of five hundred rupees or equivalent must be available before automatic payout
Autopay Feature: Freelancers can enable autopay to receive automatic monthly transfers, or opt for manual payout requests
Processing Time: Payments are processed within 3-5 business days after the payout date

The Freelancer is responsible for maintaining accurate and current banking information. The Platform is not liable for delays or failures resulting from incorrect account details provided by the Freelancer.

7. SERVICE QUALITY AND STANDARDS
The Freelancer commits to delivering services of professional quality that meet client specifications and industry standards. Failure to meet quality standards may result in negative reviews, reduced earnings, or account suspension.

The Freelancer agrees to:
- Communicate professionally and promptly with clients
- Meet agreed deadlines and project milestones
- Deliver work that is original, complete, and free of errors
- Comply with all client requirements as specified in project agreements
- Maintain confidentiality of client information and project details

8. CONFIDENTIALITY AND NON-DISCLOSURE
The Freelancer agrees to maintain strict confidentiality regarding all client information, project details, and proprietary information accessed through the Platform. This obligation extends indefinitely, even after completion of projects or termination of this Agreement.

The Freelancer shall not disclose client identities, contact information, or project specifications to third parties or utilize such information for personal benefit.

Unauthorized disclosure may result in legal action, damages claims, and account termination.

9. NON-SOLICITATION CLAUSE
The Freelancer agrees not to directly solicit or engage with clients outside the Platform to circumvent commission payments or avoid Platform oversight. Direct client solicitation discovered through Platform communications or evidence is prohibited.

Violation of this clause may result in account suspension, forfeiture of unpaid earnings, and legal action for damages. This restriction applies for 12 months following the conclusion of any client engagement through the Platform.

10. INTELLECTUAL PROPERTY
All work product created by the Freelancer for clients through Platform engagements becomes the exclusive property of the client upon payment. The Freelancer retains no rights to the final deliverable unless explicitly agreed otherwise in writing.

The Platform retains the right to display completed projects as portfolio examples, with client consent and appropriate anonymization when necessary.

11. TAXES AND REGULATORY COMPLIANCE
The Freelancer is solely responsible for understanding and complying with all applicable tax obligations, including but not limited to income tax, GST, and other statutory requirements in their jurisdiction.

The Freelancer must maintain proper records of earnings, expenses, and tax documentation. The Platform provides basic earnings reports but does not provide tax advice or prepare tax filings.

12. DISPUTE RESOLUTION
This agreement shall be governed by the laws of India. Any disputes arising under this agreement shall be subject to the jurisdiction of the courts in West Bengal and Asansol.

13. DIGITAL AGREEMENT
Upon signing this agreement digitally, a certified copy shall be provided to both the Freelancer and AAKAR Co for record purposes.

14. TERMINATION AND SEVERABILITY
This Agreement may be terminated by either party with 30 days' written notice. Upon termination, all rights and obligations cease, except those that expressly survive termination, such as confidentiality obligations.

If any provision of this Agreement is found invalid or unenforceable, such provision shall be severable and shall not affect the validity of remaining provisions.

15. GOVERNING LAW
This Agreement is governed by and construed in accordance with the laws of India, without regard to conflict of law principles. The parties consent to the exclusive jurisdiction of Indian courts.`,
};